// src/pages/bloomterminal/Account.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeContext';
import { useBiqPageContext } from '../context/BiqPageContext';
import { useUpdateUserDetailsMutation, useSetAppearanceMutation, useWhatsAppStatus, useLinkWhatsAppMutation, useRemoveWhatsAppMutation } from '../utils/api-hooks/user';
import { sendWhatsappWelcome } from '../utils/api';
import { trackEvent, EVENTS } from '../utils/analytics';
import { useGlobalFontSize } from '../context/GlobalFontSizeContext';
import ThemeModeToggle from '../components/ThemeModeToggle';
import { User, HelpCircle, Trash2, X, Loader2, ChevronDown, Search } from 'lucide-react';
import { COUNTRY_CODES } from '../constants/CountryCodes';
import PageSkeleton from '../components/PageSkeleton';
import useScrollSelectedOnOpen from '../hooks/useScrollSelectedOnOpen';

const Account = () => {
  const { t } = useTranslation();
  const { userDetails, userDetailsLoading, email } = useUser();
  const { toggleTheme, isDark } = useTheme();
  const { setPageExtras } = useBiqPageContext() || {};
  const { mutate: updateUserDetails, isPending: isSavingDetails } = useUpdateUserDetailsMutation();
  const { mutate: setAppearance } = useSetAppearanceMutation();
  const { fontSize } = useGlobalFontSize();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingSurname, setIsEditingSurname] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // WhatsApp state
  const { data: whatsAppStatus, isLoading: isLoadingWhatsApp } = useWhatsAppStatus();
  const { mutate: linkWhatsApp, isPending: isLinkingWhatsApp } = useLinkWhatsAppMutation();
  const { mutate: removeWhatsApp, isPending: isRemovingWhatsApp } = useRemoveWhatsAppMutation();
  const [isEditingWhatsApp, setIsEditingWhatsApp] = useState(false);
  const [countryCode, setCountryCode] = useState('34');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showWhatsAppTooltip, setShowWhatsAppTooltip] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [whatsAppError, setWhatsAppError] = useState('');
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const [whatsAppSuccess, setWhatsAppSuccess] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  const firstNameInputRef = useRef(null);
  const lastNameInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const tooltipRef = useRef(null);
  const countryDropdownRef = useRef(null);
  const countrySearchRef = useRef(null);
  const countryListRef = useRef(null);
  useScrollSelectedOnOpen(countryDropdownOpen, countryListRef);

  // Initialize form values from userDetails
  useEffect(() => {
    if (userDetails) {
      setFirstName(userDetails.name || '');
      setLastName(userDetails.surname || '');
    }
  }, [userDetails]);

  // Initialize WhatsApp values from status
  useEffect(() => {
    if (whatsAppStatus?.linked && whatsAppStatus?.phone) {
      setCountryCode(whatsAppStatus.phone.country_code || '34');
      // Extract national number from formatted phone if available
      const formatted = whatsAppStatus.phone.formatted || '';
      const nationalNum = formatted.replace(`+${whatsAppStatus.phone.country_code}`, '').replace(/\s/g, '');
      setPhoneNumber(nationalNum);
    }
  }, [whatsAppStatus]);

  useEffect(() => {
    if (!setPageExtras) return undefined;
    if (isLoadingWhatsApp || whatsAppStatus == null) {
      setPageExtras(null);
      return () => setPageExtras(null);
    }
    setPageExtras({ whatsapp_linked: whatsAppStatus.linked === true });
    return () => setPageExtras(null);
  }, [setPageExtras, isLoadingWhatsApp, whatsAppStatus]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingName && firstNameInputRef.current) {
      firstNameInputRef.current.focus();
    }
  }, [isEditingName]);

  useEffect(() => {
    if (isEditingSurname && lastNameInputRef.current) {
      lastNameInputRef.current.focus();
    }
  }, [isEditingSurname]);

  useEffect(() => {
    if (isEditingWhatsApp && phoneInputRef.current) {
      phoneInputRef.current.focus();
    }
  }, [isEditingWhatsApp]);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowWhatsAppTooltip(false);
      }
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus the search on open; clear it on close so a reopened list is unfiltered
  // when the selected country is scrolled into view.
  useEffect(() => {
    if (countryDropdownOpen) countrySearchRef.current?.focus();
    else setCountrySearch('');
  }, [countryDropdownOpen]);

  // Filter countries based on search
  const filteredCountries = COUNTRY_CODES.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.code.includes(countrySearch) ||
    country.iso.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Get selected country for display
  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) || COUNTRY_CODES[0];

  // Handle country selection
  const handleCountrySelect = (country) => {
    setCountryCode(country.code);
    setCountryDropdownOpen(false);
  };

  // Countdown timer for rate limit
  useEffect(() => {
    if (rateLimitCountdown <= 0) return;

    const timer = setInterval(() => {
      setRateLimitCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setWhatsAppError('');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [rateLimitCountdown]);

  // Update error message dynamically during countdown
  useEffect(() => {
    if (rateLimitCountdown > 0) {
      setWhatsAppError(t('account.whatsappRateLimitedCountdown', { seconds: rateLimitCountdown }));
    }
  }, [rateLimitCountdown, t]);

  const exitEditing = (field) => {
    if (field === 'name') {
      setIsEditingName(false);
    } else {
      setIsEditingSurname(false);
    }
  };

  // Only one field is editable at a time, so opening the other one drops any
  // text that was never saved instead of leaving it on screen as if it were.
  const startEditing = (field) => {
    setFirstName(userDetails?.name || '');
    setLastName(userDetails?.surname || '');
    setIsEditingName(field === 'name');
    setIsEditingSurname(field === 'surname');
  };

  const handleSaveDetails = (field) => {
    if (!email) return;

    const originalName = userDetails?.name || '';
    const originalSurname = userDetails?.surname || '';

    // Check if there are changes
    const nameToSave = field === 'name' ? firstName : originalName;
    const surnameToSave = field === 'surname' ? lastName : originalSurname;

    const hasNameChange = field === 'name' && firstName !== originalName;
    const hasSurnameChange = field === 'surname' && lastName !== originalSurname;

    if (!hasNameChange && !hasSurnameChange) {
      exitEditing(field);
      return;
    }

    updateUserDetails(
      { name: nameToSave, surname: surnameToSave },
      {
        onSuccess: () => {
          trackEvent(EVENTS.PROFILE_UPDATED);
          exitEditing(field);
        },
        onError: (error) => {
          console.error('Failed to update user details:', error);
          trackEvent(EVENTS.PROFILE_UPDATE_ERROR, { error: error.message });
          // Revert to original value on error
          if (field === 'name') {
            setFirstName(originalName);
          } else {
            setLastName(originalSurname);
          }
          exitEditing(field);
        },
      }
    );
  };

  const handleThemeToggle = (origin) => {
    // Toggle theme locally first for immediate feedback; persist exactly what was requested
    const newAppearance = toggleTheme(origin) === 'dark' ? 'dark_mode' : 'light_mode';

    // Then persist to backend
    setAppearance(newAppearance, {
      onError: (error) => {
        console.error('Failed to save appearance preference:', error);
        // Revert on error
        toggleTheme();
      },
    });
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveDetails(field);
    } else if (e.key === 'Escape') {
      // Revert and exit edit mode
      if (field === 'name') {
        setFirstName(userDetails?.name || '');
        setIsEditingName(false);
      } else {
        setLastName(userDetails?.surname || '');
        setIsEditingSurname(false);
      }
    }
  };

  // WhatsApp handlers
  const handleSaveWhatsApp = () => {
    setWhatsAppError('');
    setWhatsAppSuccess(false);

    if (!phoneNumber || phoneNumber.length < 6) {
      setWhatsAppError(t('account.whatsappInvalidNumber'));
      return;
    }

    linkWhatsApp(
      { countryCode, nationalNumber: phoneNumber.replace(/\s/g, '') },
      {
        onSuccess: (data) => {
          if (data.status) {
            setIsEditingWhatsApp(false);
            setWhatsAppSuccess(true);
            trackEvent(EVENTS.WHATSAPP_LINKED);
            // Send welcome message to user's WhatsApp
            sendWhatsappWelcome();
          } else {
            // Handle specific error codes
            switch (data.error_code) {
              case 'PHONE_ALREADY_LINKED':
                setWhatsAppError(t('account.whatsappAlreadyLinked'));
                break;
              case 'USER_HAS_EXISTING_PHONE':
                setWhatsAppError(t('account.whatsappHasExisting'));
                break;
              case 'RATE_LIMITED':
                const retryAfter = data.retry_after_seconds || 60;
                setRateLimitCountdown(retryAfter);
                setWhatsAppError(t('account.whatsappRateLimitedCountdown', { seconds: retryAfter }));
                break;
              default:
                setWhatsAppError(data.message || t('errors.updateFailed'));
            }
          }
        },
        onError: (error) => {
          setWhatsAppError(error.message || t('errors.updateFailed'));
        },
      }
    );
  };

  const handleDeleteWhatsApp = () => {
    removeWhatsApp(undefined, {
      onSuccess: () => {
        setShowDeleteDialog(false);
        setPhoneNumber('');
        trackEvent(EVENTS.WHATSAPP_REMOVED);
      },
      onError: (error) => {
        console.error('Failed to remove WhatsApp:', error);
      },
    });
  };

  const handleWhatsAppKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveWhatsApp();
    } else if (e.key === 'Escape') {
      setIsEditingWhatsApp(false);
      // Reset to current linked value
      if (whatsAppStatus?.linked && whatsAppStatus?.phone) {
        const formatted = whatsAppStatus.phone.formatted || '';
        const nationalNum = formatted.replace(`+${whatsAppStatus.phone.country_code}`, '').replace(/\s/g, '');
        setPhoneNumber(nationalNum);
        setCountryCode(whatsAppStatus.phone.country_code || '34');
      } else {
        setPhoneNumber('');
      }
    }
  };

  // Header shows the real account: full name, falling back to the email,
  // then to the generic page title while userDetails is still loading.
  const accountEmail = email || userDetails?.email || '';
  const fullName = [firstName || userDetails?.name, lastName || userDetails?.surname]
    .filter(Boolean)
    .join(' ')
    .trim();
  const displayName = fullName || accountEmail || t('account.headerTitle');

  if (userDetailsLoading) {
    return <PageSkeleton shape="form" />;
  }

  return (
    <div className="w-full h-full overflow-x-hidden bg-[#F0F5F2] bg-dark-app">
      {/* Main container */}
      <div className="w-full py-[32px] px-[16px] md:px-[32px]">
        {/* Main Header - shows the signed-in account */}
        <div className="flex items-center gap-[8px] mb-[40px]">
          <User fill="currentColor" size={32} className="text-[#828382] dark:text-[#828382] flex-shrink-0" />
          <h1 className="font-['Inter'] font-semibold text-[#282A29] dark:text-[#f0f5f2] truncate min-w-0" style={{ fontSize: fontSize + 4 }}>
            {displayName}
          </h1>
        </div>

        {/* Account Info Section */}
        <div className="flex flex-col gap-[24px] items-start w-full">
          {/* Section Title */}
          <h2 className="font-['Inter'] font-semibold text-[16px] leading-[24px] text-[#282A29] dark:text-[#f0f5f2]">
            {t('account.title')}
          </h2>

          {/* Input Fields */}
          <div className="flex flex-col gap-[16px] items-start w-full max-w-[448px]">
            {/* First Name Field */}
            <div className="flex items-center gap-[8px] w-full">
              <div className="bg-white dark:bg-[#282a29] h-[48px] relative rounded-[4px] flex-1 min-w-0 flex items-center px-[16px]">
                {isEditingName ? (
                  <input
                    ref={firstNameInputRef}
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'name')}
                    className="flex-1 min-w-0 bg-transparent text-[#282A29] dark:text-[#f0f5f2] font-['Inter'] font-normal outline-none placeholder:text-[#828382]"
                    style={{ fontSize: fontSize + 2 }}
                    placeholder={t('account.firstNamePlaceholder')}
                  />
                ) : (
                  <>
                    <span className="flex-1 font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]" style={{ fontSize: fontSize + 2 }}>
                      {firstName || userDetails?.name || ''}
                    </span>
                    <button
                      onClick={() => startEditing('name')}
                      className="font-['Inter'] font-normal text-bloom-green hover:text-bloom-green/80 transition-colors ml-auto"
                      style={{ fontSize: fontSize }}
                    >
                      {t('common.edit')}
                    </button>
                  </>
                )}
              </div>

              {/* Save Button (when editing) */}
              {isEditingName && (
                <button
                  onClick={() => handleSaveDetails('name')}
                  disabled={isSavingDetails}
                  className="h-[48px] px-[16px] bg-bloom-green hover:bg-bloom-green-hover disabled:opacity-50 text-[#f0f5f2] font-['Inter'] font-medium rounded-[4px] transition-colors"
                  style={{ fontSize: fontSize }}
                >
                  {isSavingDetails ? <Loader2 size={18} className="animate-spin" /> : t('common.save')}
                </button>
              )}
            </div>

            {/* Last Name Field */}
            <div className="flex items-center gap-[8px] w-full">
              <div className="bg-white dark:bg-[#282a29] h-[48px] relative rounded-[4px] flex-1 min-w-0 flex items-center px-[16px]">
                {isEditingSurname ? (
                  <input
                    ref={lastNameInputRef}
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, 'surname')}
                    className="flex-1 min-w-0 bg-transparent text-[#282A29] dark:text-[#f0f5f2] font-['Inter'] font-normal outline-none placeholder:text-[#828382]"
                    style={{ fontSize: fontSize + 2 }}
                    placeholder={t('account.lastNamePlaceholder')}
                  />
                ) : (
                  <>
                    <span className="flex-1 font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]" style={{ fontSize: fontSize + 2 }}>
                      {lastName || userDetails?.surname || ''}
                    </span>
                    <button
                      onClick={() => startEditing('surname')}
                      className="font-['Inter'] font-normal text-bloom-green hover:text-bloom-green/80 transition-colors ml-auto"
                      style={{ fontSize: fontSize }}
                    >
                      {t('common.edit')}
                    </button>
                  </>
                )}
              </div>

              {/* Save Button (when editing) */}
              {isEditingSurname && (
                <button
                  onClick={() => handleSaveDetails('surname')}
                  disabled={isSavingDetails}
                  className="h-[48px] px-[16px] bg-bloom-green hover:bg-bloom-green-hover disabled:opacity-50 text-[#f0f5f2] font-['Inter'] font-medium rounded-[4px] transition-colors"
                  style={{ fontSize: fontSize }}
                >
                  {isSavingDetails ? <Loader2 size={18} className="animate-spin" /> : t('common.save')}
                </button>
              )}
            </div>

            {/* Email Field (Read-only) */}
            <div className="bg-white dark:bg-[#282a29] h-[48px] relative rounded-[4px] w-full flex items-center px-[16px]">
                <span className="flex-1 font-['Inter'] font-normal text-[16px] leading-[24px] text-[#282A29] dark:text-[#f0f5f2]" style={{ fontSize: fontSize + 2 }}>
                {email || userDetails?.email || ''}
              </span>
            </div>
          </div>

          {/* Separator Line between Account Info and WhatsApp */}
          <div className="w-full max-w-[448px] h-[1px] bg-[#E8EBE9] dark:bg-[#3d3d3d] my-[8px]"></div>

            {/* WhatsApp Number Section */}
            <div className="w-full max-w-[448px]">
              {/* WhatsApp Label with Tooltip */}
              <div className="flex items-center gap-[8px] mb-[12px]">
                <span className="font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]" style={{ fontSize: fontSize }}>
                  {t('account.whatsappNumber')}
                </span>
                <div className="relative flex items-center" ref={tooltipRef}>
                  <button
                    onClick={() => setShowWhatsAppTooltip(!showWhatsAppTooltip)}
                    className="flex items-center justify-center text-bloom-green hover:text-bloom-green-hover transition-colors"
                  >
                    <HelpCircle size={16} className="block" />
                  </button>
                  {showWhatsAppTooltip && (
                    <div className="absolute left-0 bottom-full mb-2 z-50 w-64 bg-[#282a29] text-[#f0f5f2] text-xs p-3 rounded-lg shadow-lg border border-[#5d625f]">
                      {t('account.whatsappTooltip')}
                      <div className="absolute left-4 -bottom-1 w-2 h-2 bg-[#282a29] border-r border-b border-[#5d625f] transform rotate-45" />
                    </div>
                  )}
                </div>
              </div>

              {/* WhatsApp Input */}
              <div className="flex items-center gap-[8px]">
                {/* Country Code Dropdown */}
                <div className="relative" ref={countryDropdownRef}>
                  <button
                    type="button"
                    onClick={() => {
                      if (isEditingWhatsApp || !whatsAppStatus?.linked) {
                        setCountryDropdownOpen(!countryDropdownOpen);
                      }
                    }}
                    disabled={!isEditingWhatsApp && whatsAppStatus?.linked}
                    className="bg-white dark:bg-[#282a29] h-[48px] rounded-[4px] flex items-center px-[12px] min-w-[100px] gap-1 disabled:cursor-default disabled:opacity-100"
                  >
                    <span className="text-[#282A29] dark:text-[#f0f5f2] font-['Inter']" style={{ fontSize: fontSize }}>
                      {selectedCountry.flag} +{selectedCountry.code}
                    </span>
                    {(isEditingWhatsApp || !whatsAppStatus?.linked) && (
                      <ChevronDown size={14} className="text-[#828382]" />
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {countryDropdownOpen && (
                    <div className="absolute top-[52px] left-0 z-50 bg-white dark:bg-[#282a29] border border-[#C3C4C3] dark:border-[#828382] rounded-[8px] shadow-lg w-[280px] max-h-[300px] overflow-hidden">
                      {/* Search Input */}
                      <div className="p-2 border-b border-[#C3C4C3] dark:border-[#828382]">
                        <div className="flex items-center gap-2 bg-[#F0F5F2] dark:bg-[#191919] rounded-[4px] px-2 py-1.5">
                          <Search size={14} className="text-[#828382]" />
                          <input
                            ref={countrySearchRef}
                            type="text"
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            placeholder={t('common.search')}
                            className="flex-1 bg-transparent text-[#282A29] dark:text-[#f0f5f2] font-['Inter'] text-sm outline-none placeholder:text-[#828382]"
                          />
                        </div>
                      </div>

                      {/* Country List */}
                      <div ref={countryListRef} className="overflow-y-auto max-h-[240px]">
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <button
                              key={`${country.iso}-${country.code}`}
                              data-selected={country.code === countryCode && country.iso === selectedCountry.iso}
                              type="button"
                              onClick={() => handleCountrySelect(country)}
                              className={`w-full px-3 py-2 text-left hover:bg-[#F0F5F2] dark:hover:bg-[#191919] flex items-center gap-2 ${
                                country.code === countryCode && country.iso === selectedCountry.iso
                                  ? 'bg-bloom-green/10'
                                  : ''
                              }`}
                            >
                              <span className="text-lg">{country.flag}</span>
                              <span className="flex-1 text-[#282A29] dark:text-[#f0f5f2] font-['Inter'] text-sm truncate">
                                {country.name}
                              </span>
                              <span className="text-[#828382] font-['Inter'] text-sm">
                                +{country.code}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-4 text-center text-[#828382] text-sm">
                            {t('common.noResults')}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Phone Number Input */}
                <div className="bg-white dark:bg-[#282a29] h-[48px] relative rounded-[4px] flex-1 flex items-center px-[16px]">
                  {isEditingWhatsApp || (!whatsAppStatus?.linked && !isLoadingWhatsApp) ? (
                    <input
                      ref={phoneInputRef}
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => {
                        // Only allow numbers
                        const value = e.target.value.replace(/[^0-9]/g, '');
                        setPhoneNumber(value);
                        setWhatsAppError('');
                      }}
                      onKeyDown={handleWhatsAppKeyDown}
                      className="flex-1 bg-transparent text-[#282A29] dark:text-[#f0f5f2] font-['Inter'] font-normal outline-none placeholder:text-[#828382]"
                      style={{ fontSize: fontSize + 2 }}
                      placeholder={t('account.whatsappPlaceholder')}
                    />
                  ) : (
                    <span className="flex-1 font-['Inter'] font-normal text-[#282A29] dark:text-[#f0f5f2]" style={{ fontSize: fontSize + 2 }}>
                      {isLoadingWhatsApp ? '...' : (whatsAppStatus?.phone?.formatted?.replace(`+${whatsAppStatus?.phone?.country_code} `, '') || '')}
                    </span>
                  )}

                  {/* Edit Button */}
                  {whatsAppStatus?.linked && !isEditingWhatsApp && (
                    <button
                      onClick={() => {
                        setIsEditingWhatsApp(true);
                        setWhatsAppSuccess(false);
                      }}
                      className="font-['Inter'] font-normal text-bloom-green hover:text-bloom-green/80 transition-colors ml-[8px]"
                      style={{ fontSize: fontSize }}
                    >
                      {t('common.edit')}
                    </button>
                  )}
                </div>

                {/* Delete Button (when linked) */}
                {whatsAppStatus?.linked && !isEditingWhatsApp && (
                  <button
                    onClick={() => setShowDeleteDialog(true)}
                    className="h-[48px] w-[48px] flex items-center justify-center text-[#828382] hover:text-red-500 transition-colors"
                  >
                    <Trash2 size={20} />
                  </button>
                )}

                {/* Save Button (when editing or adding new) */}
                {(isEditingWhatsApp || (!whatsAppStatus?.linked && phoneNumber)) && (
                  <button
                    onClick={handleSaveWhatsApp}
                    disabled={isLinkingWhatsApp || rateLimitCountdown > 0}
                    className="h-[48px] px-[16px] bg-bloom-green hover:bg-bloom-green-hover disabled:opacity-50 text-[#f0f5f2] font-['Inter'] font-medium rounded-[4px] transition-colors"
                    style={{ fontSize: fontSize }}
                  >
                    {isLinkingWhatsApp ? <Loader2 size={18} className="animate-spin" /> : t('common.save')}
                  </button>
                )}
              </div>

              {/* Error Message */}
              {whatsAppError && (
                <p className="mt-[8px] text-red-500 text-sm font-['Inter']">
                  {whatsAppError}
                </p>
              )}

              {/* Success Message */}
              {whatsAppSuccess && !isEditingWhatsApp && (
                <div className="mt-[8px] px-4 py-3 bg-bloom-green/20 border border-bloom-green rounded-[8px]">
                  <p className="font-['Inter'] text-bloom-green" style={{ fontSize: fontSize }}>
                    {t('account.whatsappSuccessMessage')}
                  </p>
                </div>
              )}
            </div>
        </div>

        {/* Separator Line */}
        <div className="w-full max-w-[448px] h-[1px] bg-[#E8EBE9] dark:bg-[#3d3d3d] my-[32px]"></div>

        {/* Appearance Section */}
        <div className="flex flex-col gap-[24px] items-start w-full">
          {/* Section Title */}
          <h2 className="font-['Inter'] font-semibold text-[16px] leading-[24px] text-[#282A29] dark:text-[#f0f5f2]">
            {t('account.appearance')}
          </h2>

          {/* Theme Toggle */}
          <div className="flex items-center gap-[16px]">
            <span className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#282A29] dark:text-[#f0f5f2]">
              {t('account.lightMode')}
            </span>

            <ThemeModeToggle isDark={isDark} onToggle={handleThemeToggle} />

            <span className="font-['Inter'] font-normal text-[14px] leading-[21px] text-[#282A29] dark:text-[#f0f5f2]">
              {t('account.darkMode')}
            </span>
          </div>
        </div>
      </div>

      {/* Delete WhatsApp Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-[#282a29] rounded-lg p-[24px] max-w-[400px] w-full mx-[16px] relative">
            {/* Close Button */}
            <button
              onClick={() => setShowDeleteDialog(false)}
              className="absolute top-[16px] right-[16px] text-[#828382] hover:text-[#282A29] dark:hover:text-[#f0f5f2] transition-colors"
            >
              <X size={20} />
            </button>

            {/* Dialog Content */}
            <h3 className="font-['Inter'] font-semibold text-[18px] text-[#282A29] dark:text-[#f0f5f2] mb-[16px] pr-[24px]">
              {t('account.whatsappDeleteTitle')}
            </h3>
            <p className="font-['Inter'] text-[14px] text-[#5D625F] dark:text-[#828382] mb-[24px]">
              {t('account.whatsappDeleteMessage')}
            </p>

            {/* Dialog Actions */}
            <div className="flex justify-end gap-[12px]">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="px-[20px] py-[10px] font-['Inter'] font-medium text-[14px] text-[#282A29] dark:text-[#f0f5f2] border border-[#E8EBE9] dark:border-[#3d3d3d] rounded-[4px] hover:bg-[#F0F5F2] dark:hover:bg-[#3d3d3d] transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteWhatsApp}
                disabled={isRemovingWhatsApp}
                className="px-[20px] py-[10px] font-['Inter'] font-medium text-[14px] text-[#f0f5f2] bg-bloom-green hover:bg-bloom-green-hover disabled:opacity-50 rounded-[4px] transition-colors"
              >
                {isRemovingWhatsApp ? <Loader2 size={16} className="animate-spin" /> : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
